import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../lib/axios';

const TimerContext = createContext();

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

export const TimerProvider = ({ children }) => {
  const [activeTimers, setActiveTimers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchActiveTimers = useCallback(async () => {
    try {
      const res = await api.get('/time/active-timers');
      setActiveTimers(res.data || []);
    } catch (error) {
      // Silently fail - user might not have permission or not be logged in yet
      setActiveTimers([]);
    }
  }, []);

  const startTimer = useCallback(async (taskId, projectId = null) => {
    setLoading(true);
    try {
      const payload = { task_id: taskId };
      if (projectId) payload.project_id = projectId;
      
      await api.post('/time/start', payload);
      await fetchActiveTimers();
      
      // Dispatch event to notify header to show timer details
      window.dispatchEvent(new CustomEvent('timer-started', { detail: { taskId, projectId } }));
      
      return true;
    } catch (error) {
      console.error('Error starting timer:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchActiveTimers]);

  const stopTimer = useCallback(async (timerId) => {
    setLoading(true);
    try {
      await api.post(`/time/${timerId}/stop`);
      await fetchActiveTimers();
      
      window.dispatchEvent(new CustomEvent('timer-stopped', { detail: { timerId } }));
      
      return true;
    } catch (error) {
      console.error('Error stopping timer:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchActiveTimers]);

  const pauseTimer = useCallback(async (timerId) => {
    try {
      await api.post(`/time/${timerId}/pause`);
      await fetchActiveTimers();
      return true;
    } catch (error) {
      console.error('Error pausing timer:', error);
      throw error;
    }
  }, [fetchActiveTimers]);

  const resumeTimer = useCallback(async (timerId) => {
    try {
      await api.post(`/time/${timerId}/resume`);
      await fetchActiveTimers();
      return true;
    } catch (error) {
      console.error('Error resuming timer:', error);
      throw error;
    }
  }, [fetchActiveTimers]);

  // Initial fetch
  useEffect(() => {
    fetchActiveTimers();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchActiveTimers, 30000);
    return () => clearInterval(interval);
  }, [fetchActiveTimers]);

  const value = {
    activeTimers,
    loading,
    startTimer,
    stopTimer,
    pauseTimer,
    resumeTimer,
    fetchActiveTimers,
    hasActiveTimer: activeTimers.length > 0
  };

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
};

export default TimerContext;
